/**
 * MediaLoader - Handles loading media from Google Cloud Storage
 */

import { Scene } from '@babylonjs/core/scene';
import { VideoTexture } from '@babylonjs/core/Materials/Textures/videoTexture';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { Constants } from '@babylonjs/core/Engines/constants';
import { SceneLoader } from '@babylonjs/core/Loading/sceneLoader';
import { Tools } from '@babylonjs/core/Misc/tools';
import '@babylonjs/loaders/glTF';
import type { MediaItem } from './MediaConfig';
import type { SkyboxConfig, TextureVariant } from '../ui/UIManager';
import { DeviceDetector } from '../utils/DeviceDetector';
import { StreamingVideoLoader } from './StreamingVideoLoader';
import { Logger } from '../utils/Logger';

const logger = Logger.getLogger('MediaLoader');

export class MediaLoader {
  private scene: Scene;
  /** Track HLS loaders for cleanup */
  private streamingLoaders: Map<string, StreamingVideoLoader> = new Map();

  constructor(scene: Scene) {
    this.scene = scene;
  }

  /**
   * Load a 360 video from URL with retry logic
   */
  async loadVideo(mediaItem: MediaItem, retryCount: number = 0, onProgress?: (percent: number) => void): Promise<VideoTexture> {
    const MAX_RETRIES = 3;
    const RETRY_DELAYS = [1000, 2000, 4000]; // Exponential backoff in ms
    const LOAD_TIMEOUT = 30000; // 30 second timeout

    try {
      const videoTexture = await this.attemptVideoLoad(mediaItem, LOAD_TIMEOUT, onProgress);
      logger.info(`Video loaded successfully: ${mediaItem.title}`);
      return videoTexture;
    } catch (error) {
      logger.error(`Video load attempt ${retryCount + 1}/${MAX_RETRIES + 1} failed:`, error);

      // If we haven't exceeded max retries, try again
      if (retryCount < MAX_RETRIES) {
        const delay = RETRY_DELAYS[retryCount];
        logger.debug(`Retrying video load in ${delay}ms...`);
        await this.sleep(delay);
        return this.loadVideo(mediaItem, retryCount + 1, onProgress);
      }

      // All retries exhausted
      throw new Error(`Failed to load video after ${MAX_RETRIES + 1} attempts: ${mediaItem.title}`);
    }
  }

  /**
   * Single attempt to load a video with timeout
   */
  private attemptVideoLoad(mediaItem: MediaItem, timeout: number, onProgress?: (percent: number) => void): Promise<VideoTexture> {
    return new Promise((resolve, reject) => {
      let timeoutId: NodeJS.Timeout | null = null;
      let isResolved = false;

      try {
        const videoTexture = new VideoTexture(
          mediaItem.id,
          mediaItem.url,
          this.scene,
          false, // generateMipMaps
          true,  // invertY
          VideoTexture.TRILINEAR_SAMPLINGMODE,
          {
            autoPlay: true,
            loop: true,
            autoUpdateTexture: true,
            poster: mediaItem.thumbnail,
            muted: true, // CRITICAL: Must be muted for autoplay to work
          }
        );

        // Set video attributes for better compatibility
        videoTexture.video.crossOrigin = 'anonymous';
        videoTexture.video.playsInline = true;
        videoTexture.video.preload = 'auto';

        logger.debug(` Video element created for ${mediaItem.title}, waiting for events...`);

        // Debug event listeners
        videoTexture.video.addEventListener('loadstart', () => {
          logger.debug(` loadstart: ${mediaItem.title}`);
        });
        videoTexture.video.addEventListener('loadedmetadata', () => {
          logger.debug(` loadedmetadata: ${mediaItem.title} - duration: ${videoTexture.video.duration}s`);
        });
        videoTexture.video.addEventListener('loadeddata', () => {
          logger.debug(` loadeddata: ${mediaItem.title} - readyState: ${videoTexture.video.readyState}`);
        });
        videoTexture.video.addEventListener('waiting', () => {
          logger.debug(` waiting: ${mediaItem.title} - buffering...`);
        });
        videoTexture.video.addEventListener('stalled', () => {
          logger.debug(` stalled: ${mediaItem.title} - download stalled`);
        });

        // Progress handler
        const onVideoProgress = () => {
          if (videoTexture.video.duration > 0 && videoTexture.video.buffered.length > 0 && onProgress) {
            const bufferedEnd = videoTexture.video.buffered.end(videoTexture.video.buffered.length - 1);
            const duration = videoTexture.video.duration;
            const percent = (bufferedEnd / duration) * 100;
            onProgress(percent);
          }
        };

        if (onProgress) {
          videoTexture.video.addEventListener('progress', onVideoProgress);
        }

        // Define event handlers first
        // Use loadeddata (readyState >= 2) instead of canplaythrough for faster loading
        // canplaythrough waits for full buffer which can take forever on large videos
        const onVideoReady = () => {
          logger.debug(` Video ready event: ${mediaItem.title} (readyState: ${videoTexture.video.readyState}, isResolved: ${isResolved})`);
          if (isResolved) return;

          // HAVE_CURRENT_DATA (2) or better is sufficient to start playback
          if (videoTexture.video.readyState >= 2) {
            isResolved = true;
            logger.debug(` ✅ Video ready to play: ${mediaItem.title} (readyState: ${videoTexture.video.readyState})`);
            if (onProgress) onProgress(100);
            cleanup();
            resolve(videoTexture);
          }
        };

        // Legacy handler for canplaythrough (still useful as backup)
        const onCanPlayThrough = () => {
          logger.debug(` canplaythrough event: ${mediaItem.title} (readyState: ${videoTexture.video.readyState}, isResolved: ${isResolved})`);
          if (isResolved) return;
          isResolved = true;

          logger.debug(` ✅ Video ready to play (canplaythrough): ${mediaItem.title}`);
          if (onProgress) onProgress(100);
          cleanup();
          resolve(videoTexture);
        };

        const onError = () => {
          if (isResolved) return;
          isResolved = true;

          const video = videoTexture.video;
          const errorDetails = video.error ? {
            code: video.error.code,
            message: video.error.message,
            MEDIA_ERR_ABORTED: video.error.code === 1,
            MEDIA_ERR_NETWORK: video.error.code === 2,
            MEDIA_ERR_DECODE: video.error.code === 3,
            MEDIA_ERR_SRC_NOT_SUPPORTED: video.error.code === 4,
          } : 'Unknown error';

          logger.error(`Video error for ${mediaItem.title}:`, errorDetails);
          cleanup();
          reject(new Error(`Video load error: ${JSON.stringify(errorDetails)}`));
        };

        // Define cleanup function after handlers
        const cleanup = () => {
          if (timeoutId) clearTimeout(timeoutId);
          videoTexture.video.removeEventListener('loadeddata', onVideoReady);
          videoTexture.video.removeEventListener('canplaythrough', onCanPlayThrough);
          videoTexture.video.removeEventListener('error', onError);
          if (onProgress) {
            videoTexture.video.removeEventListener('progress', onVideoProgress);
          }
        };

        // Listen for loadeddata (fires earlier than canplaythrough)
        videoTexture.video.addEventListener('loadeddata', onVideoReady);
        // Also keep canplaythrough as backup
        videoTexture.video.addEventListener('canplaythrough', onCanPlayThrough, { once: true });
        videoTexture.video.addEventListener('error', onError);

        // Check if already ready (in case events fired before listeners attached)
        if (videoTexture.video.readyState >= 2) {
          logger.debug(` Video already ready on create: ${mediaItem.title} (readyState: ${videoTexture.video.readyState})`);
          onVideoReady();
        }

        // Timeout handler
        timeoutId = setTimeout(() => {
          if (isResolved) return;
          isResolved = true;

          logger.error(`Video load timeout after ${timeout}ms: ${mediaItem.title}`);
          cleanup();
          reject(new Error(`Video load timeout after ${timeout}ms`));
        }, timeout);

      } catch (error) {
        logger.error('Error creating video texture:', error);
        if (timeoutId) clearTimeout(timeoutId);
        reject(error);
      }
    });
  }

  /**
   * Helper: Sleep for specified milliseconds
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Load a video with HLS streaming support
   * Automatically uses HLS if hlsUrl is provided, falls back to progressive MP4
   *
   * @param mediaItem - Media item with url (MP4 fallback) and optional hlsUrl (HLS manifest)
   * @param onProgress - Progress callback (0-100)
   * @returns VideoTexture ready for use with Babylon.js
   *
   * @example
   * // Self-hosted MP4 (free users)
   * loadStreamingVideo({ id: '1', url: 'https://cdn.example.com/video.mp4' })
   *
   * // User's own Mux HLS (free users with Mux)
   * loadStreamingVideo({ id: '1', url: '...fallback...', hlsUrl: 'https://stream.mux.com/abc.m3u8' })
   *
   * // Your Mux HLS (paid users)
   * loadStreamingVideo({ id: '1', url: '...', hlsUrl: 'https://stream.mux.com/xyz.m3u8' })
   */
  async loadStreamingVideo(
    mediaItem: MediaItem,
    onProgress?: (percent: number) => void
  ): Promise<VideoTexture> {
    // If no HLS URL, use progressive loading
    if (!mediaItem.hlsUrl) {
      logger.debug(` 📹 Video source: Progressive MP4`);
      logger.debug(`    URL: ${mediaItem.url}`);
      logger.debug(`    Title: ${mediaItem.title}`);
      return this.loadVideo(mediaItem, 0, onProgress);
    }

    // Check HLS support
    if (!StreamingVideoLoader.supportsHLS()) {
      logger.warn(' 📹 Video source: HLS NOT SUPPORTED - falling back to Progressive MP4');
      logger.debug(`    HLS URL was: ${mediaItem.hlsUrl}`);
      logger.debug(`    Fallback URL: ${mediaItem.url}`);
      return this.loadVideo(mediaItem, 0, onProgress);
    }

    // Detect if this is a Mux URL
    const isMux = mediaItem.hlsUrl.includes('stream.mux.com') || mediaItem.hlsUrl.includes('mux.com');
    logger.debug(` 📹 Video source: HLS Streaming ${isMux ? '(Mux)' : '(Generic HLS)'}`);
    logger.debug(`    HLS URL: ${mediaItem.hlsUrl}`);
    logger.debug(`    Fallback MP4: ${mediaItem.url || 'none'}`);
    logger.debug(`    Title: ${mediaItem.title}`);

    // Create video element
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.playsInline = true;
    video.muted = true; // Required for autoplay
    video.autoplay = true;
    video.loop = true;

    // iOS-specific attributes
    if (DeviceDetector.isIOS()) {
      video.setAttribute('playsinline', 'true');
      video.setAttribute('webkit-playsinline', 'true');
    }

    const loader = new StreamingVideoLoader();

    try {
      // Load HLS manifest and wait for it to be ready
      await loader.loadHLS(mediaItem.hlsUrl, video);

      // Store loader for later cleanup
      this.streamingLoaders.set(mediaItem.id, loader);

      // Create VideoTexture from the pre-configured video element
      // Note: Babylon.js VideoTexture can accept an HTMLVideoElement directly
      const videoTexture = new VideoTexture(
        mediaItem.id,
        video, // Pass the video element, not URL
        this.scene,
        false, // generateMipMaps
        true,  // invertY
        VideoTexture.TRILINEAR_SAMPLINGMODE,
        {
          autoPlay: true,
          loop: true,
          autoUpdateTexture: true,
          poster: mediaItem.thumbnail,
        }
      );

      // Report progress based on buffer state
      if (onProgress) {
        const reportProgress = () => {
          const bufferInfo = loader.getBufferInfo();
          // Report based on first 10 seconds buffered (for large files)
          const targetBuffer = Math.min(10, bufferInfo.duration || 10);
          const percent = Math.min(100, (bufferInfo.buffered / targetBuffer) * 100);
          onProgress(percent);
        };

        video.addEventListener('progress', reportProgress);
        video.addEventListener('canplaythrough', () => onProgress(100), { once: true });
      }

      logger.debug(` HLS video loaded successfully: ${mediaItem.title}`);
      return videoTexture;

    } catch (error) {
      // Clean up failed loader
      loader.dispose();
      this.streamingLoaders.delete(mediaItem.id);

      logger.warn('HLS failed, falling back to progressive MP4:', error);

      // Fallback to progressive loading
      return this.loadVideo(mediaItem, 0, onProgress);
    }
  }

  /**
   * Dispose of streaming video loader (call when video is no longer needed)
   */
  disposeStreamingVideo(id: string): void {
    const loader = this.streamingLoaders.get(id);
    if (loader) {
      loader.dispose();
      this.streamingLoaders.delete(id);
      logger.debug(` Disposed streaming loader: ${id}`);
    }
  }

  /**
   * Get streaming loader for a video (if using HLS)
   */
  getStreamingLoader(id: string): StreamingVideoLoader | undefined {
    return this.streamingLoaders.get(id);
  }

  /**
   * Load a 360 image from URL with progress reporting
   */
  async loadImage(mediaItem: MediaItem, onProgress?: (percent: number) => void): Promise<Texture> {
    return new Promise((resolve, reject) => {
      // Use Tools.LoadFile to fetch data with progress events
      Tools.LoadFile(
        mediaItem.url,
        (data) => {
          // Success callback
          try {
            let blob: Blob;
            if (data instanceof ArrayBuffer) {
              blob = new Blob([data]);
            } else {
              // Fallback for string data (shouldn't happen with useArrayBuffer=true)
              blob = new Blob([data]);
            }

            const blobUrl = URL.createObjectURL(blob);

            const texture = new Texture(
              blobUrl,
              this.scene,
              false, // noMipmap
              false, // invertY - images need to be flipped
              Texture.TRILINEAR_SAMPLINGMODE,
              () => {
                logger.debug(`Image loaded: ${mediaItem.title}`);
                URL.revokeObjectURL(blobUrl); // Clean up memory
                if (onProgress) onProgress(100);
                resolve(texture);
              },
              (message) => {
                logger.error(`Error creating texture from blob: ${mediaItem.title}`, message);
                URL.revokeObjectURL(blobUrl);
                reject(new Error(`Failed to create texture: ${mediaItem.title}`));
              }
            );
          } catch (error) {
            reject(error);
          }
        },
        (event) => {
          // Progress callback
          if (event.lengthComputable && event.total > 0 && onProgress) {
            const percent = (event.loaded / event.total) * 100;
            onProgress(percent);
          }
        },
        this.scene.offlineProvider,
        true, // useArrayBuffer
        (_request, exception) => {
          // Error callback
          logger.error(`Error downloading image: ${mediaItem.title}`, exception);
          reject(exception || new Error(`Failed to download image: ${mediaItem.title}`));
        }
      );
    });
  }

  /**
   * Load a KTX2 compressed texture
   * Babylon.js 8.x+ automatically transcodes to native GPU formats (ASTC/BC7/ETC2)
   */
  async loadKTX2Image(mediaItem: MediaItem, onProgress?: (percent: number) => void): Promise<Texture> {
    return new Promise((resolve, reject) => {
      // KTX2 textures are loaded the same way as regular textures
      // Babylon.js automatically detects .ktx2 extension and uses transcoder
      const texture = new Texture(
        mediaItem.url,
        this.scene,
        false, // noMipmap - KTX2 files include mipmaps
        false, // invertY - KTX2 handles orientation internally
        Constants.TEXTURE_TRILINEAR_SAMPLINGMODE,
        () => {
          logger.debug(`KTX2 texture loaded: ${mediaItem.title} (transcoded to native GPU format)`);
          if (onProgress) onProgress(100);
          resolve(texture);
        },
        (_message, exception) => {
          logger.error(`KTX2 texture load failed: ${mediaItem.title}`, exception);
          reject(exception || new Error(`Failed to load KTX2 texture: ${mediaItem.title}`));
        }
      );
    });
  }

  /**
   * Load image with smart variant selection
   * Automatically selects best texture based on device type (desktop/mobile/tablet/vr)
   */
  async loadImageWithVariants(
    skyboxConfig: SkyboxConfig,
    mediaItem: MediaItem,
    onProgress?: (percent: number) => void
  ): Promise<Texture> {
    // 1. Determine device type
    const deviceType = DeviceDetector.getTextureVariant();

    // 2. Select best variant (with fallback chain)
    let variant: TextureVariant | null = null;

    if (skyboxConfig.variants) {
      // Try device-specific variant first
      variant = skyboxConfig.variants[deviceType] || skyboxConfig.variants.fallback;

      logger.debug(
        `Selected ${deviceType} variant: ${variant.format} @ ${variant.resolution || 'unknown'}`
      );
    } else if (skyboxConfig.url) {
      // Legacy single URL - auto-detect format from extension
      let format: TextureVariant['format'];
      if (skyboxConfig.url.match(/\.(ktx2)$/i)) {
        format = 'ktx2-uastc';
      } else if (skyboxConfig.url.match(/\.(jpg|jpeg)$/i)) {
        format = 'jpg';
      } else {
        format = 'png';
      }

      variant = {
        url: skyboxConfig.url,
        format
      };

      logger.debug(` Using legacy URL (${format}): ${skyboxConfig.url}`);
    }

    if (!variant) {
      // Fallback: Try to find any available variant or use legacy URL
      const availableVariants = skyboxConfig.variants;
      if (availableVariants) {
        // Try each variant in priority order
        variant = availableVariants.desktop ||
                  availableVariants.vr ||
                  availableVariants.tablet ||
                  availableVariants.mobile ||
                  availableVariants.fallback;
      }

      // Still no variant? Try legacy URL
      if (!variant && skyboxConfig.url) {
        let format: TextureVariant['format'];
        if (skyboxConfig.url.match(/\.(ktx2)$/i)) {
          format = 'ktx2-uastc';
        } else if (skyboxConfig.url.match(/\.(jpg|jpeg)$/i)) {
          format = 'jpg';
        } else {
          format = 'png';
        }
        variant = { url: skyboxConfig.url, format };
      }

      if (!variant) {
        throw new Error('No texture variant available for this skybox');
      }

      logger.debug(` Using fallback variant for ${deviceType}:`, variant);
    }

    // 3. Load based on format with fallback
    const textureMediaItem: MediaItem = {
      ...mediaItem,
      url: variant.url
    };

    if (variant.format.startsWith('ktx2')) {
      try {
        return await this.loadKTX2Image(textureMediaItem, onProgress);
      } catch (ktx2Error) {
        logger.warn('KTX2 load failed, trying fallback...', ktx2Error);

        // Try fallback variants in order: desktop -> fallback -> legacy URL
        const fallbackVariant = skyboxConfig.variants?.desktop ||
                                skyboxConfig.variants?.fallback;

        if (fallbackVariant && !fallbackVariant.format.startsWith('ktx2')) {
          logger.debug(` Using fallback variant: ${fallbackVariant.format} @ ${fallbackVariant.resolution || 'unknown'}`);
          const fallbackMediaItem: MediaItem = {
            ...mediaItem,
            url: fallbackVariant.url
          };
          return this.loadImage(fallbackMediaItem, onProgress);
        } else if (skyboxConfig.url && !skyboxConfig.url.match(/\.(ktx2)$/i)) {
          // Try legacy URL if it's not KTX2
          logger.debug(` Using legacy URL as fallback: ${skyboxConfig.url}`);
          const legacyMediaItem: MediaItem = {
            ...mediaItem,
            url: skyboxConfig.url
          };
          return this.loadImage(legacyMediaItem, onProgress);
        }

        // No fallback available, re-throw original error
        throw ktx2Error;
      }
    } else {
      // JPG/PNG - use existing image loader
      return this.loadImage(textureMediaItem, onProgress);
    }
  }

  /**
   * Load a 3D model (glTF/GLB) from URL
   */
  async loadModel(mediaItem: MediaItem, onProgress?: (percent: number) => void) {
    try {
      logger.debug(`Loading model: ${mediaItem.title}`);

      const result = await SceneLoader.ImportMeshAsync(
        '',           // meshNames (empty = load all)
        '',           // rootUrl (will be extracted from url)
        mediaItem.url,
        this.scene,
        (event) => {
          if (event.lengthComputable && onProgress) {
            const percent = (event.loaded / event.total) * 100;
            onProgress(percent);
          }
        },
        '.glb'        // file extension
      );

      logger.debug(`Model loaded: ${mediaItem.title}`);
      return result;

    } catch (error) {
      logger.error(`Error loading model: ${mediaItem.title}`, error);
      throw new Error(`Failed to load model: ${mediaItem.title}`);
    }
  }

  /**
   * Preload an image for thumbnails
   */
  async preloadThumbnail(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = url;
    });
  }

  /**
   * Check if URL is accessible (CORS check)
   */
  async checkMediaAccess(url: string): Promise<boolean> {
    try {
      const response = await fetch(url, { method: 'HEAD' });
      return response.ok;
    } catch (error) {
      logger.error('Media access check failed:', error);
      return false;
    }
  }
}
